import { useEffect, useId } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Spinner } from '@rocco/components/spinner';
import { Textarea } from '@rocco/components/textarea';
import type { GetModerationReasonsQuery } from 'generated/graphql';
import { useUserModeration } from '~/modules/moderation/hooks/use-user-moderation';
import type { Participant } from '../../../types';

const formSchema = z.object({
    moderationReasonID: z.string().min(1, 'Reason is required'),
    note: z
        .string()
        .min(1, 'Notes are required')
        .max(1000, 'Maximum 1000 characters'),
});

export function UserReportDialog({
    open,
    conversationID,
    participant,
    onOpenChange,
}: {
    open: boolean;
    conversationID: number;
    participant: Participant;
    onOpenChange: (open: boolean) => void;
}) {
    const participantName =
        participant.Identity.__typename === 'Professional'
            ? participant.Identity.Title
            : participant.Identity.Name;
    const formReactId = useId();
    const formId = `user-report-dialog-form-${formReactId}`;
    const { moderationReasons, isLoading, isSubmitting, reportUser } =
        useUserModeration({
            onReportSuccess: () => {
                toast.success(`User "${participantName}" has been reported`);
                onOpenChange(false);
            },
            onReportError: (message) => {
                toast.error(message);
            },
        });
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onSubmit',
        defaultValues: { moderationReasonID: '', note: '' },
        disabled: isSubmitting,
    });

    function onSubmit(values: z.infer<typeof formSchema>) {
        // TODO: Handle reporting professionals.
        reportUser(
            participant.Identity.ID,
            values.moderationReasonID,
            values.note,
            {
                conversation_id: conversationID,
                participant_id: Number(participant.ID),
            }
        );
    }

    // Reset form when the dialog is opened.
    useEffect(() => {
        if (open) {
            form.reset();
        }
    }, [open, form]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Report user "{participantName}"</DialogTitle>
                    <DialogDescription>
                        Select the reason for reporting the user.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form
                        id={formId}
                        className="space-y-4"
                        onSubmit={form.handleSubmit(onSubmit)}
                    >
                        <FormField
                            name="moderationReasonID"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="ml-1 !text-4 !leading-tight"
                                        errorVariant="errorDarker"
                                    >
                                        Reason
                                    </FormLabel>
                                    <FormControl>
                                        <Select
                                            name={field.name}
                                            value={field.value}
                                            disabled={field.disabled}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger
                                                className="w-full !text-[16px] !leading-tight md:!text-4"
                                                color="lightOutline"
                                                text="default"
                                            >
                                                <SelectValue placeholder="Select a reason" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {moderationReasons.map(
                                                    (reason) => (
                                                        <ModerationReasonSelectItem
                                                            key={reason.ID}
                                                            reason={reason}
                                                        />
                                                    )
                                                )}
                                                {isLoading &&
                                                    moderationReasons.length ===
                                                        0 && (
                                                        <div className="flex items-center justify-center py-2">
                                                            <Spinner className="size-5 text-black/50" />
                                                        </div>
                                                    )}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage
                                        className="!text-3.5 !leading-tight"
                                        variant="darker"
                                    />
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="note"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="ml-1 !text-4 !leading-tight"
                                        errorVariant="errorDarker"
                                    >
                                        Notes
                                    </FormLabel>
                                    <FormControl>
                                        <Textarea
                                            className="!text-[16px] !leading-normal md:!text-4"
                                            color="lightOutline"
                                            text="default"
                                            placeholder="Enter any additional information about the user that you would like to report."
                                            rows={3}
                                            autoComplete="off"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage
                                        className="!text-3.5 !leading-tight"
                                        variant="darker"
                                    />
                                </FormItem>
                            )}
                        />
                    </form>
                </Form>
                <DialogFooter>
                    <Button
                        color="outline"
                        disabled={form.formState.disabled}
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form={formId}
                        isLoading={form.formState.disabled}
                        disabled={form.formState.disabled}
                    >
                        Report
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ModerationReasonSelectItem({
    reason,
}: {
    reason: GetModerationReasonsQuery['moderationReasons'][number];
}) {
    return reason.Children.length ? (
        <SelectGroup>
            <SelectLabel className="!text-3 font-medium leading-tight">
                {reason.Label}
            </SelectLabel>
            {reason.Children.map((child) => (
                <SelectItem key={child.ID} value={child.ID}>
                    {child.Label}
                </SelectItem>
            ))}
        </SelectGroup>
    ) : (
        <>
            <SelectSeparator />
            <SelectItem value={reason.ID}>{reason.Label}</SelectItem>
        </>
    );
}
